import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';

import useSearch from '../../hooks/useSearch';
import { useTabs } from '../../hooks/useTabs';
import { useSaveSettings } from '../../hooks/useSaveSettings';
import useReloadState from '../../hooks/useReloadState';
import { CATEGORIES, SUBCATEGORIES } from '../../common/categories';
import { fieldsReducer, initFieldsState, mapToSettings } from './generalReducer';

import { filterFieldsBySystemMode, isDirtyTab } from '../../common/utils';
import Category from '../../components/Category/Category';
import CompanyInfo from '../../components/CompanyInfo/CompanyInfo';
import SMTPServer from '../../components/SMTPServer/SMTPServer';
import RadiusClients from '../../components/RadiusClients/RadiusClients';
import FailClose from '../../components/FailClose/FailClose';
import SelectiveForwarding from '../../components/SelectiveForwarding/SelectiveForwarding';
import { debounce, get } from 'lodash';
import { USER_ENGAGEMENT_PAGE_KEYS } from '@silverfort-nodelibs/common/utils/consts';
import { exportCsv, httpPost } from '../../../utils';
import { RADIUS_FIELDS } from '../../common/fields';
import { useVisibleTabs } from '../../hooks/lockedFeatures/useVisibleTabs';

const { GENERAL } = CATEGORIES;
const { COMPANY_INFO, RADIUS_CLIENTS, SMTP_SERVER, FAIL_CLOSE, SELECTIVE_FORWARDING } = SUBCATEGORIES;

const RADIUS_CLIENTS_CSV_FIELDS = [
    {
        title: RADIUS_FIELDS.ID.NAME,
        path: RADIUS_FIELDS.ID.ID   
    },
    {
        title: RADIUS_FIELDS.IP_ADDRESS.NAME,
        path: RADIUS_FIELDS.IP_ADDRESS.ID
    },
    {
        title: RADIUS_FIELDS.ENABLE_RADIUS_OTP.NAME,
        path: RADIUS_FIELDS.ENABLE_RADIUS_OTP.ID,
        transformer: (OTPValue) => OTPValue || false
    }
];
const General = ({ saveFunc, disableSave, systemMode }) => {
    const [wizardSaved, setWizardSaved] = useState(false);
    const settings = useSelector((state) => state.settings.settings);

    const [fields, dispatch] = useReducer(fieldsReducer, { settings }, initFieldsState);
    const visibleFields = useSearch(useVisibleTabs(fields));
    const [selectedTab, setSelectedTab] = useTabs(GENERAL, visibleFields);
    const [save, discard] = useSaveSettings(saveFunc, settings, mapToSettings, fields, selectedTab, dispatch);
    useReloadState(settings, dispatch, wizardSaved, setWizardSaved);

    const saveWizard = useCallback(
        (newSettings) => {
            setWizardSaved(true);
            return saveFunc(newSettings, true, true);
        },
        [saveFunc]
    );

    const exportTabToCsv = (settingsFieldKey, fieldsConfig, fileName = 'export') => {
        const arrayValues = Object.values(get(settings, settingsFieldKey, {}));
        exportCsv(arrayValues, fieldsConfig, fileName);
    };

    let bottomActions;
    switch (selectedTab) {
        case RADIUS_CLIENTS.ID:
            bottomActions = [
                {
                    name: 'Export RADIUS clients',
                    clicked: debounce(() => {
                        exportTabToCsv('RADIUSClients', RADIUS_CLIENTS_CSV_FIELDS, 'radius-clients');
                    }, 500)
                }
            ];
            break;
        default:
            break;
    }

    let content = null;
    if (visibleFields[COMPANY_INFO.ID] && selectedTab === COMPANY_INFO.ID) {
        content = (
            <CompanyInfo
                fields={filterFieldsBySystemMode(visibleFields[COMPANY_INFO.ID].fields, systemMode)}
                dispatch={dispatch}
            />
        );
    } else if (visibleFields[RADIUS_CLIENTS.ID] && selectedTab === RADIUS_CLIENTS.ID) {
        content = <RadiusClients fields={visibleFields[RADIUS_CLIENTS.ID].fields} dispatch={dispatch} />;
    } else if (visibleFields[SMTP_SERVER.ID] && selectedTab === SMTP_SERVER.ID) {
        content = (
            <SMTPServer
                fields={visibleFields[SMTP_SERVER.ID].fields}
                dispatch={dispatch}
                isDirty={isDirtyTab(visibleFields[SMTP_SERVER.ID])}
            />
        );
    } else if (visibleFields[FAIL_CLOSE.ID] && selectedTab === FAIL_CLOSE.ID) {
        content = (
            <FailClose
                fields={visibleFields[FAIL_CLOSE.ID].fields}
                dispatch={dispatch}
                maxSelectedSubnets={settings.MaxSelectedSubnetsInPacketFwdSetting}
                systemMode={systemMode}
            />
        );
    } else if (visibleFields[SELECTIVE_FORWARDING.ID] && selectedTab === SELECTIVE_FORWARDING.ID) {
        content = (
            <SelectiveForwarding
                fields={visibleFields[SELECTIVE_FORWARDING.ID].fields}
                dispatch={dispatch}
                maxSelectedSubnets={settings.MaxSelectedSubnetsInPacketFwdSetting}
            />
        );
    }

    useEffect(() => {
        httpPost('/data/userEngagement', { pageId: USER_ENGAGEMENT_PAGE_KEYS.SETTINGS_GENERAL });
    }, []);

    return (
        <Category
            category={GENERAL}
            fields={fields}
            visibleFields={visibleFields}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            saveSettings={save}
            saveWizard={saveWizard}
            discard={() => discard(false)}
            discardAll={() => discard(true)}
            actions={bottomActions}
            disableSave={disableSave}>
            {content}
        </Category>
    );
};

export default General;
